/**
 * Paused host (spec D41). A brand admin can pause a host on that brand
 * (`brand_memberships.listingsSuspended`); the host still reads `isHost` and
 * `hostStatus = APPROVED`, so the API adds `User.hostListingsSuspended` for the
 * request brand. The web checks it before every hosting action (client only:
 * the server does not refuse these writes).
 */
export type HostAccountStatus = 'active' | 'paused' | 'notActive';

export interface HostAccountFlags {
  isHost?: boolean | null;
  hostListingsSuspended?: boolean | null;
}

/**
 * `paused` when the brand paused the host, `notActive` when the user is not a host
 * on this brand (removed, rejected), else `active`. Only call it for host mode.
 */
export function hostAccountStatus(flags: HostAccountFlags | null | undefined): HostAccountStatus {
  if (flags?.hostListingsSuspended) return 'paused';
  if (!flags?.isHost) return 'notActive';
  return 'active';
}

export interface HostAccountNotice {
  title: string;
  body: string;
  /** "Email support@…" when the brand has a support address, else null. */
  contactLine: string | null;
  supportEmail: string | null;
  /** Short label for the listing badge. */
  badge: string;
}

/** Modal and notice words for a host who is not active on the brand. */
export function hostAccountNotice(
  status: Exclude<HostAccountStatus, 'active'>,
  brand: { name: string; supportEmail?: string | null },
): HostAccountNotice {
  const supportEmail = brand.supportEmail?.trim() || null;
  const contactLine = supportEmail ? `Email ${supportEmail}` : null;
  if (status === 'paused') {
    return {
      title: 'Host account paused',
      body: `Your host account on ${brand.name} is paused. Please contact the ${brand.name} admin for more help.`,
      contactLine,
      supportEmail,
      badge: 'Paused',
    };
  }
  return {
    title: 'Host account not active',
    body: `Your host account on ${brand.name} is not active. Please contact the ${brand.name} admin for more help.`,
    contactLine,
    supportEmail,
    badge: 'Not active',
  };
}
